"use client";

import React, { useState, useEffect, useRef } from "react";
import useGoogle from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { DataView } from "primereact/dataview";
import PropTypes from "prop-types";
import { Toast } from "primereact/toast";
import { isNotEmpty, parseHTML, STRAPI_ASSETS_BASE_PATH } from "./utils";
import { getConstants } from "./langUtils";
import TrainerCard from "./TrainerCard";
import CenterCard from "./CenterCard";
import SendMessageForm from "./SendMessageForm";
import Loader from "./Loader";
import fetchGRPCAPI from "./api";
import "./styles/heartspot.css";
import "./styles/heartspotmain.css";
import "bootstrap/dist/css/bootstrap.min.css";
const rightArrow = `${STRAPI_ASSETS_BASE_PATH}right_arrow_e517f81a9d.png`;
const leftArrow = `${STRAPI_ASSETS_BASE_PATH}left_arrow_e1e2f05293.png`;
const noLocationIcon = `${STRAPI_ASSETS_BASE_PATH}info_95f4d34a57.png`;

function HfnHeartSpot(props) {
  const {
    Title,
    Description,
    Locale: locale,
    base_url,
    google_map_api_key,
  } = props;
  const [value, setValue] = useState("");
  const [predictionListFlag, setPredictionListFlag] = useState(false);
  const [rangeValue, setRangeValue] = useState(5);
  const [rangeUnit, setRangeUnit] = useState("KM");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [displaySection, setDisplaySection] = useState("trainers");
  const [userLocation, setUserLocation] = useState(null);
  const [trainersResponse, setTrainersResponse] = useState(null);
  const [centersResponse, setCentersResponse] = useState(null);
  const [trainersTotalItems, setTrainersTotalItems] = useState(0);
  const [meditationCentersTotalItems, setMeditationCentersTotalItems] =
    useState(0);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [locationAccess, setLocationAccess] = useState(false);
  const [assignPartnerId, setAssigPartnerId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clearHandle, setClearHandle] = useState(false);
  const BASE_URL = base_url;
  const [trainersPageNo, setTrainersPageNo] = useState(0);
  const [centersPageNo, setCentersPageNo] = useState(0);
  const [noDataMsg, setNoDataMsg] = useState(getConstants(locale)?.NODATAMSG);
  const [noTrainerMsg, setNoTrainerMsg] = useState(
    getConstants(locale)?.NOTRAINERMSG
  );
  const [noCenterMsg, setNoCenterMsg] = useState(
    getConstants(locale)?.NOCENTERMSG
  );
  const toast = useRef(null);
  const trainersContainerRef = useRef(null);
  const {
    placesService,
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading,
  } = useGoogle({
    apiKey: google_map_api_key,
    debounce: 600, // debounce is set up for 600 ms
  });

  const onSelectListItem = async (item) => {
    await placesService.getDetails(
      {
        placeId: item.place_id,
      },
      (placeDetails) => {
        setSelectedPlace(placeDetails);
      }
    );
    setValue(item.description);
    setPredictionListFlag(false);
  };

  useEffect(() => {
    if (placePredictions?.length > 0) setPredictionListFlag(true);
  }, [placePredictions]);

  const scrollToFirstTrainerCard = () => {
    if (trainersContainerRef.current) {
      const yOffset = -150;
      const y =
        trainersContainerRef.current.getBoundingClientRect().top +
        window.scrollY +
        yOffset;

      if (y > 0) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: y, behavior: "smooth" });
        });
      }
    }
  };

  useEffect(() => {
    if (
      displaySection === "trainers" &&
      (!trainersResponse?.trainers || trainersResponse.trainers.length === 0)
    ) {
      setDisplaySection((prev) => (prev === "trainers" ? "meditation" : prev));
    } else if (
      displaySection === "meditation" &&
      (!centersResponse?.meditationCenters ||
        centersResponse.meditationCenters.length === 0)
    ) {
      setDisplaySection((prev) => (prev === "meditation" ? "trainers" : prev));
    }
  }, [trainersResponse?.trainers, centersResponse?.meditationCenters]);

  useEffect(() => {
    if (
      trainersResponse?.trainers?.length > 0 ||
      centersResponse?.meditationCenters?.length > 0
    ) {
      setTimeout(() => {
        scrollToFirstTrainerCard();
      }, 100);
    }
  }, [
    trainersResponse?.trainers?.length,
    trainersPageNo,
    centersResponse?.meditationCenters?.length,
    centersPageNo,
  ]);

  const handleClear = () => {
    setValue("");
    setRangeValue(5);
    setRangeUnit("KM");
    if (clearHandle) {
      setUserLocation(null);
      setTrainersResponse(null);
      setCentersResponse(null);
      setTrainersTotalItems(0);
      setMeditationCentersTotalItems(0);
      setNoDataMsg(null);
      setNoTrainerMsg(null);
      setNoCenterMsg(null);
    }
  };

  const openDialog = (trainerId) => {
    setAssigPartnerId(trainerId);
    setDialogVisible(true);
  };

  const handleSectionChange = (section) => {
    setDisplaySection(section);
  };
  useEffect(() => {
    if (!locationAccess) {
      toast.current?.show({
        severity: "info",
        content: (
          <div className="d-flex align-items-center" style={{ color: "black" }}>
            <img
              src={noLocationIcon}
              alt="no_location_icon"
              priority
              width={28}
              height={28}
              style={{ marginRight: "10px" }}
            />
            <span>{getConstants(locale)?.LOCATIONACCESSMSG}</span>
          </div>
        ),
        className: "custom-toast",
        sticky: true,
        life: 0,
      });
    } else {
      toast.current?.clear(); // Closes the toast when locationAccess is available
    }
  }, [locationAccess]);

  const handleSearch = () => {
    const locationInput = document.getElementsByClassName("location-map")[0];
    if (value === "") {
      locationInput.focus();
    } else {
      setUserLocation(null);
      setTrainersResponse(null);
      setCentersResponse(null);
      setTrainersTotalItems(0);
      setMeditationCentersTotalItems(0);
      setTrainersPageNo(0);
      setCentersPageNo(0);
      setClearHandle(true);
      const location = selectedPlace ? selectedPlace.geometry.location : null;
      const latitude = location ? location.lat() : null;
      const longitude = location ? location.lng() : null;
      const distance = rangeValue;
      // const distanceUnit = document.getElementById("distanceUnitSelect").value;

      if (latitude && longitude && distance) {
        setUserLocation({ latitude, longitude });
        setRangeValue(distance);
        // setRangeUnit(distanceUnit);
      }
    }
  };
  useEffect(() => {
    const getCurrentLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ latitude, longitude });
            setLocationAccess(true);
          },
          () => {
            // console.error("Error getting current location:", error);
            setLocationAccess(false);
          }
        );
      } else {
        // console.error("Geolocation is not supported by this browser.");
        setLocationAccess(false);
      }
    };
    getCurrentLocation();
  }, []);

  const fetchData = async (endpoint, updatedPageNo = 0) => {
    setLoading(true);
    try {
      const result = await fetchGRPCAPI(
        endpoint,
        userLocation,
        rangeValue,
        rangeUnit,
        updatedPageNo,
        endpoint === "/trainers" ? 9 : 6,
        base_url
      );

      if (!result) return false;
      const { data } = result;
      if (endpoint === "/trainers") {
        setTrainersResponse(data);
        setTrainersTotalItems(data.totalCount || 0);
        if (data.totalCount > 0) {
          setLocationAccess(true);
        }
        if ((data.totalCount || 0) === 0) {
          setLocationAccess(true);
          setNoTrainerMsg(getConstants(locale)?.NOTRAINERMSG);
        }
      } else if (endpoint === "/centers") {
        setCentersResponse(data);
        setMeditationCentersTotalItems(data.totalCount || 0);
        if (data.totalCount > 0) {
          setLocationAccess(true);
        }
        if ((data.totalCount || 0) === 0) {
          setLocationAccess(true);
          setNoCenterMsg(getConstants(locale)?.NOCENTERMSG);
        }
      }
      if (
        (!data?.trainers || data.trainers.length === 0) &&
        (!data?.centers || data.centers.length === 0)
      ) {
        setNoDataMsg(getConstants(locale)?.NODATAMSG);
      }
    } catch (error) {
      // console.error("Error:", error);
    } finally {
      setLoading(false);
    }
    return true;
  };

  const handleTrainersNext = () => {
    if ((trainersPageNo + 1) * 9 < trainersTotalItems) {
      const updatedPageNo = trainersPageNo + 1;
      setTrainersPageNo(updatedPageNo);
      fetchData("/trainers", updatedPageNo);
    }
  };

  const handleTrainersPrevious = () => {
    if (trainersPageNo > 0) {
      const updatedPageNo = trainersPageNo - 1;
      setTrainersPageNo(updatedPageNo);
      fetchData("/trainers", updatedPageNo);
    }
  };

  const handleCentersNext = () => {
    if (!centersResponse || !centersResponse.meditationCenters) return;
    if ((centersPageNo + 1) * 6 < meditationCentersTotalItems) {
      const updatedPageNo = centersPageNo + 1;
      setCentersPageNo(updatedPageNo);
      fetchData("/centers", updatedPageNo);
    }
  };

  const handleCentersPrevious = () => {
    if (!centersResponse || !centersResponse.meditationCenters) return;
    if (centersPageNo > 0) {
      const updatedPageNo = centersPageNo - 1;
      setCentersPageNo(updatedPageNo);
      fetchData("/centers", updatedPageNo);
    }
  };

  const fetchDataOnLoad = async () => {
    try {
      await Promise.all([fetchData("/trainers"), fetchData("/centers")]);
    } catch (error) {
      // console.error("Error fetching data on load:", error);
    }
  };

  useEffect(() => {
    fetchDataOnLoad();
  }, [userLocation]);

  const trainerItemTemplate = (trainer) => (
    <TrainerCard
      key={trainer?.id}
      trainer={trainer}
      onOpenDialog={openDialog}
      rangeDispUnit={rangeUnit}
      locale={locale}
    />
  );
  const centerItemTemplate = (meditation) => (
    <CenterCard
      key={meditation.id}
      meditation={meditation}
      onOpenDialog={openDialog}
      locale={locale}
    />
  );
  return (
    <>
      {loading && <Loader />}
      <Toast ref={toast} />
      <div className="container mt-5">
        <div className="Heartspot-wrapper">
          <div className="Heartspot-wrapper-title text-center">
            <h1
              className="heartspot-title"
              dangerouslySetInnerHTML={parseHTML(Title)}
            />
          </div>
          <div className="heartspot-content">
            <div className="row justify-content-center">
              <div
                className="col-md-12"
                dangerouslySetInnerHTML={parseHTML(Description)}
              />
            </div>
          </div>
          <div className="heartspot-search">
            <div className="form-row row justify-content-center">
              <div className="col-md-10 search-box">
                <div className="row searchbox">
                  <div className="form-group col-lg-5 location">
                    {/* <label htmlFor="locationinput">
                      {getConstants(locale)?.LOCATION}
                      <br /> */}
                    <div className="position-relative">
                      <i className="map-marker position-absolute searchmarker" />
                      <input
                        type="text"
                        value={value}
                        id="locationinput"
                        className="form-control location-map"
                        placeholder={`${getConstants(locale)?.SEARCH} ${
                          getConstants(locale)?.LOCATION
                        }`}
                        autoComplete="off"
                        onChange={(evt) => {
                          getPlacePredictions({ input: evt.target.value });
                          setValue(evt.target.value);
                        }}
                        loading={isPlacePredictionsLoading}
                        style={{ paddingLeft: "28px" }}
                      />
                      {isNotEmpty(value) && (
                        <button
                          type="button"
                          icon="pi pi-times"
                          onClick={handleClear}
                          className="p-button-text cross-icon"
                        >
                          <span className="p-button-icon p-c pi pi-times" />
                          <span className="p-button-label p-c">&nbsp;</span>
                        </button>
                      )}
                    </div>
                    {/* </label> */}
                    {predictionListFlag && (
                      <div className="predictions-wrapper">
                        <ul className="predictions-list">
                          {!isPlacePredictionsLoading &&
                            placePredictions?.map((item) => {
                              return (
                                <li key={item.id}>
                                  <button
                                    type="button"
                                    onClick={() => onSelectListItem(item)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        onSelectListItem(item);
                                      }
                                    }}
                                    className="prediction-item"
                                    style={{
                                      all: "unset",
                                      display: "block",
                                      width: "100%",
                                      textAlign: "left",
                                    }}
                                  >
                                    <p>{item.description}</p>
                                  </button>
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="form-group col-lg-7 range p-0">
                    <div className="distance-sec">
                      <div className="distanceContainer d-flex">
                        <select
                          id="distanceUnitSelect"
                          className="km-miles"
                          name="distanceUnit"
                          onChange={(e) => setRangeUnit(e.target.value)}
                          value={rangeUnit}
                          aria-label="distanceUnitSelect"
                        >
                          <option value="KM">{getConstants(locale)?.KM}</option>
                          <option value="MILES">
                            {getConstants(locale)?.MILES}
                          </option>
                        </select>
                        <input
                          type="number"
                          id="rangeinput"
                          value={rangeValue}
                          onChange={(e) => setRangeValue(e.target.value)}
                          min="5"
                          className="input-range"
                        />
                      </div>
                      <div className="distance-bar">
                        <input
                          type="range"
                          className="w-100"
                          name="distance"
                          id="customRange2"
                          value={rangeValue}
                          onChange={(e) => setRangeValue(e.target.value)}
                          min="5"
                        />
                      </div>
                      <div className="heartspot-buttons">
                        <button
                          type="button"
                          onClick={handleSearch}
                          className="btn btn-outline-dark search-result text-uppercase"
                        >
                          {getConstants(locale)?.SEARCH}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {(trainersResponse?.trainers ||
            centersResponse?.meditationCenters) && (
            <div className="trainers-centre">
              <div className="trainers-centre-button">
                <button
                  type="button"
                  className={`btn btn-outline-dark ${
                    displaySection === "trainers" ? "active" : "btn-inactive"
                  }`}
                  onClick={() => handleSectionChange("trainers")}
                >
                  {getConstants(locale)?.TRAINER}({trainersTotalItems})
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-dark ${
                    displaySection === "meditation" ? "active" : "btn-inactive"
                  }`}
                  onClick={() => handleSectionChange("meditation")}
                >
                  {getConstants(locale)?.MEDITATIONCENTER}(
                  {meditationCentersTotalItems})
                </button>
              </div>
            </div>
          )}
          <div
            className="trainers-meditation-res my-4"
            ref={trainersContainerRef}
          >
            {displaySection === "trainers" && trainersResponse?.trainers && (
              <div className="heartspot-list-disp">
                <div className="card-group row">
                  <DataView
                    value={trainersResponse.trainers}
                    itemTemplate={trainerItemTemplate}
                    // paginator="true"
                    rows={9}
                  />
                </div>
              </div>
            )}
            {displaySection === "meditation" &&
              centersResponse?.meditationCenters && (
                <div className="meditation-centers-disp">
                  <div className="card-deck row">
                    <DataView
                      value={centersResponse.meditationCenters}
                      itemTemplate={centerItemTemplate}
                      // paginator="true"
                      rows={6}
                    />
                  </div>
                </div>
              )}
            {!loading &&
              locationAccess &&
              (!isNotEmpty(centersResponse?.meditationCenters) ||
                centersResponse?.meditationCenters === undefined) &&
              (!isNotEmpty(trainersResponse?.trainers) ||
                trainersResponse?.trainers === undefined) && (
                <strong className="text-center">{noDataMsg}</strong>
              )}
            {locationAccess &&
              displaySection === "trainers" &&
              (!isNotEmpty(trainersResponse?.trainers) ||
                trainersResponse?.trainers === undefined) &&
              isNotEmpty(centersResponse?.meditationCenters) && (
                <strong className="text-center">{noTrainerMsg}</strong>
              )}

            {locationAccess &&
              displaySection === "meditation" &&
              isNotEmpty(trainersResponse?.trainers) &&
              (!isNotEmpty(centersResponse?.meditationCenters) ||
                centersResponse?.meditationCenters === undefined) && (
                <strong className="text-center">{noCenterMsg}</strong>
              )}
            <div className="next-previous-btn text-right my-2">
              {displaySection === "trainers" && trainersResponse?.trainers && (
                <>
                  <button
                    type="button"
                    onClick={handleTrainersPrevious}
                    className="btn"
                    disabled={trainersPageNo === 0}
                  >
                    <img
                      src={leftArrow}
                      alt="Left Arrow"
                      priority={false}
                      width={27}
                      height={16}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={handleTrainersNext}
                    className="btn"
                    disabled={(trainersPageNo + 1) * 9 >= trainersTotalItems}
                  >
                    <img
                      src={rightArrow}
                      alt="Right Arrow"
                      priority={false}
                      width={27}
                      height={16}
                    />
                  </button>
                </>
              )}

              {displaySection === "meditation" &&
                centersResponse?.meditationCenters && (
                  <>
                    <button
                      type="button"
                      onClick={handleCentersPrevious}
                      className="btn"
                      disabled={centersPageNo === 0}
                    >
                      <img
                        src={leftArrow}
                        alt="Left Arrow"
                        priority={false}
                        width={27}
                        height={16}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={handleCentersNext}
                      className="btn"
                      disabled={
                        (centersPageNo + 1) * 6 >= meditationCentersTotalItems
                      }
                    >
                      <img
                        src={rightArrow}
                        alt="Right Arrow"
                        priority={false}
                        width={27}
                        height={16}
                      />
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>

      <SendMessageForm
        dialogVisible={dialogVisible}
        setDialogVisible={setDialogVisible}
        assignPartnerId={assignPartnerId}
        selectedPlace={selectedPlace}
        locale={locale}
        BASE_URL={BASE_URL}
        loading={loading}
        setLoading={setLoading}
      />
    </>
  );
}
HfnHeartSpot.defaultProps = { Description: null };
HfnHeartSpot.propTypes = {
  Title: PropTypes.string.isRequired,
  Description: PropTypes.string,
  Locale: PropTypes.string.isRequired,
  base_url: PropTypes.string.isRequired,
  google_map_api_key: PropTypes.string.isRequired,
};

export default HfnHeartSpot;
